import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { AppDialog } from "@/components/app/AppDialog";
import { Button } from "@/components/base/buttons/button";
import { useAcknowledgeVersion, useVersionNotice } from "@/lib/versionNotice";
import { sanitizeServerHtml } from "@/lib/sanitizeHtml";

/**
 * Dialog po povýšení aplikace: co je v nové verzi nového, s odkazem na celý changelog
 * v nápovědě.
 *
 * Ukáže se **jednou na uživatele a verzi** — server rozhoduje podle `last_seen_version`,
 * klient jen vykreslí. Zavření (i křížkem) verzi potvrdí, takže dialog nemůže uváznout
 * v cyklu „zavřu, znovu se objeví".
 *
 * Uživateli, který ještě žádnou verzi neviděl, se dialog nezobrazí a hodnota se mu jen
 * tiše zapíše — jinak by první upozornění po nasazení téhle funkce oznamovalo samo sebe.
 */
export function VersionNoticeDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useVersionNotice();
  const acknowledge = useAcknowledgeVersion();
  const [dismissed, setDismissed] = useState(false);
  const silentAckSent = useRef(false);

  // Účet bez známé verze: potvrdit na pozadí, aby příští povýšení dialog ukázalo.
  useEffect(() => {
    if (data && !data.show && data.previousVersion == null && !silentAckSent.current) {
      silentAckSent.current = true;
      acknowledge.mutate();
    }
  }, [data, acknowledge]);

  const safeHtml = useMemo(
    () => (data?.changelogHtml ? sanitizeServerHtml(data.changelogHtml) : ""),
    [data?.changelogHtml],
  );

  if (!data?.show || dismissed) {
    return null;
  }

  const close = () => {
    setDismissed(true);
    acknowledge.mutate();
  };

  return (
    <AppDialog
      isOpen
      onClose={close}
      title={t("versionNotice.title", { version: data.version })}
      description={t("versionNotice.subtitle", { previous: data.previousVersion ?? "?" })}
      // Changelog je souvislý text s dlouhými odrážkami; v užším dialogu se láme po pár
      // slovech a odrážky se rozpadají na příliš mnoho řádků.
      size="3xl"
      footer={
        <>
          <Button
            size="sm"
            color="secondary"
            onPress={() => {
              close();
              void navigate({ to: "/docs/$slug", params: { slug: data.docsSlug } });
            }}
          >
            {t("versionNotice.viewAll")}
          </Button>
          <Button size="sm" onPress={close}>
            {t("versionNotice.dismiss")}
          </Button>
        </>
      }
    >
      {safeHtml ? (
        // Obsah je server-renderovaný z našeho vlastního changelogu; DOMPurify je
        // defenzivní pojistka, stejně jako na stránce nápovědy.
        <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: safeHtml }} />
      ) : (
        // Verze bez záznamu v changelogu — odkaz do nápovědy zůstává.
        <p className="text-sm text-secondary">{t("versionNotice.noChangelog")}</p>
      )}
    </AppDialog>
  );
}
